import { useCallback, useEffect, useRef, useState } from "react";
import { approveRefundRequest, rejectRefundRequest } from "Config/API/action";
import { getRefundRequests } from "Config/API/get";
import useStripeOnboardingGuard from "Hooks/useStripeOnboardingGuard";
import { toast } from "react-toastify";
import useFullPageLoader from "Hooks/useFullPageLoader";

const DONE_TYPING_INTERVAL = 1500;

const INFO_APPROVAL_MESSAGES = [
  "In-sufficient Credit",
  "Refund Already approved Successfully",
];

/**
 * Shared logic of the refund request pages (organization and super admin):
 * loading, searching, filtering and approving/rejecting refund requests.
 */
const useRefundRequests = () => {
  const typingTimer = useRef(null);
  const token = localStorage.getItem("token");
  const [loader, setLoader, resetLoader] = useFullPageLoader();
  const [requestList, setRequestList] = useState([]);
  const [searchedText, setSearchedText] = useState("");
  const [filterType, setFilterType] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { isOnboardingRequired, stripeDashboardUrl } =
    useStripeOnboardingGuard();
  const [showStripeOnboardingPopup, setShowStripeOnboardingPopup] =
    useState(false);

  const getData = useCallback(
    async (filter = "", searchKeywords = "") => {
      if (!token) return;

      setLoader();

      try {
        const res = await getRefundRequests(token, searchKeywords, filter);

        if (res?.status === 200) {
          setRequestList(res?.data?.data || []);
        } else {
          setRequestList([]);
        }
      } catch (error) {
        console.error("Error fetching refund requests:", error);
        toast.error("Unable to load refund requests");
        setRequestList([]);
      } finally {
        resetLoader();
      }
    },
    [token, resetLoader, setLoader]
  );

  useEffect(() => {
    getData(filterType, searchedText);
  }, [filterType, searchedText, getData]);

  useEffect(() => {
    if (isOnboardingRequired) {
      setShowStripeOnboardingPopup(true);
    }
  }, [isOnboardingRequired]);

  useEffect(() => {
    return () => {
      if (typingTimer.current) {
        clearTimeout(typingTimer.current);
      }
    };
  }, []);

  const handleSearchInput = (value) => {
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
    }

    typingTimer.current = setTimeout(() => {
      setSearchedText(value);
    }, DONE_TYPING_INTERVAL);
  };

  const filterData = (value) => {
    setFilterType(value === "all" ? "" : value);
  };

  const changeStatus = async (status, data) => {
    if (isActionLoading || !data?.id) return;

    setIsActionLoading(true);

    try {
      const response =
        status === "approve"
          ? await approveRefundRequest(token, data.id)
          : await rejectRefundRequest(token, data.id);

      if (!response?.status) {
        toast(response?.message || "Something went wrong", { type: "error" });
        return;
      }

      if (INFO_APPROVAL_MESSAGES.includes(response?.message)) {
        toast(response.message, { type: "info" });
      } else {
        toast(response.message || "Refund request updated", {
          type: "success",
        });
      }

      await getData(filterType, searchedText);
    } catch (error) {
      console.error("Error updating refund status:", error);
      toast("Something went wrong while updating the refund request", {
        type: "error",
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  return {
    loader,
    requestList,
    handleSearchInput,
    filterData,
    changeStatus,
    showStripeOnboardingPopup,
    setShowStripeOnboardingPopup,
    stripeDashboardUrl,
  };
};

export default useRefundRequests;
